import {
  selectVendorCommissionBandIdsByBusinessIdLookup,
  selectVendorCommissionBandIdsByDeploymentIdLookup,
} from "./indexes";
import {
  getEntryKeyIsUUID,
  objectMapper,
  createLookupCombiner,
  createLookupSelectors,
} from "./lib";
import { bestFitForSale } from "lib/sale";
import { getConsignments, getVendorCommissionBands } from "./root";
import { EMPTY_ARRAY, EMPTY_OBJECT } from "lib";
import { getCurrentSale } from "./currentSale";

export const vendorCommissionBandDeploymentMapper = (
  vendorCommissionBandIds: string[],
  vendorCommissionBands: Record<string, VendorCommissionBand>,
) =>
  vendorCommissionBandIds
    .filter(cbId => !vendorCommissionBands[cbId].businessId)
    .map(cbId => vendorCommissionBands[cbId]);

export const [
  selectVendorCommissionBandsByDeploymentIdLookup,
  getVendorCommissionBandsByDeploymentId,
] = createLookupSelectors(
  [selectVendorCommissionBandIdsByDeploymentIdLookup, getVendorCommissionBands],
  createLookupCombiner(vendorCommissionBandDeploymentMapper),
  EMPTY_ARRAY,
);

export const [
  selectVendorCommissionBandsByBusinessIdLookup,
  getVendorCommissionBandsByBusinessId,
] = createLookupSelectors(
  [selectVendorCommissionBandIdsByBusinessIdLookup, getVendorCommissionBands],
  createLookupCombiner(objectMapper, getEntryKeyIsUUID),
  EMPTY_ARRAY,
);

export const [
  selectVendorCommissionForCurrentSaleByDeploymentIdLookup,
  getVendorCommissionForCurrentSaleByDeploymentId,
] = createLookupSelectors(
  [selectVendorCommissionBandsByDeploymentIdLookup, getCurrentSale],
  (
    vendorCommissionBandsByDeploymentIdLookup: Record<
      number,
      VendorCommissionBand[]
    >,
    currentSale: Sale | null,
  ) => {
    if (!currentSale) return EMPTY_OBJECT;
    return Object.values(currentSale.deployment_sales).reduce(
      (acc: Record<number, Commission | null>, ds: DeploymentSale) => {
        if (ds.deployment_id) {
          acc[ds.deployment_id] = bestFitForSale(
            currentSale,
            vendorCommissionBandsByDeploymentIdLookup[ds.deployment_id],
          );
        }
        return acc;
      },
      {},
    );
  },
);

export const [
  selectSaleVendorCommissionByDeploymentIdLookup,
  getSaleVendorCommissionByDeploymentId,
] = createLookupSelectors([getCurrentSale], (currentSale: Sale | null) => {
  if (!currentSale) return EMPTY_OBJECT;
  return Object.values(currentSale.deployment_sales).reduce(
    (acc: Record<number, Commission | null>, ds: DeploymentSale) => {
      acc[ds.deployment_id] = ds.vendorCommission || null;
      return acc;
    },
    {},
  );
});

export const [
  selectVendorCommissionForCurrentSaleByBusinessIdLookup,
  getVendorCommissionForCurrentSaleByBusinessId,
] = createLookupSelectors(
  [
    selectVendorCommissionBandsByBusinessIdLookup,
    getCurrentSale,
    getConsignments,
  ],
  (
    vendorCommissionBandsByBusinessIdLookup: Record<
      string,
      VendorCommissionBand[]
    >,
    currentSale: Sale | null,
    consignments: Record<string, Consignment>,
  ) => {
    if (!currentSale || !consignments) return EMPTY_OBJECT;
    return Object.values(consignments).reduce(
      (acc: Record<string, Commission | null>, consignment: Consignment) => {
        if (consignment.vendor_id) {
          acc[consignment.vendor_id] = bestFitForSale(
            currentSale,
            vendorCommissionBandsByBusinessIdLookup[consignment.vendor_id],
          );
        }
        return acc;
      },
      {},
    );
  },
);
